import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const TIMELINE_STATUS_ACTION_ID = 'timeline.node.change-status'

type TimelineCandidate = { id: string; title: string; type: string }
type TimelineStatusArgs = {
  prompt: string
  selectedTargets: TimelineCandidate[]
  nameCandidates: TimelineCandidate[]
}

export type TimelineStatusConfirmPayload = {
  kind: 'TIMELINE_STATUS_CHANGE'
  status: string
  targetIds: string[]
}

const candidateSchema: JSONSchemaType<TimelineCandidate> = {
  type: 'object',
  properties: { id: { type: 'string' }, title: { type: 'string' }, type: { type: 'string' } },
  required: ['id', 'title', 'type'],
  additionalProperties: false,
}

const timelineStatusArgsSchema: JSONSchemaType<TimelineStatusArgs> = {
  type: 'object',
  properties: {
    prompt: { type: 'string' },
    selectedTargets: { type: 'array', items: candidateSchema },
    nameCandidates: { type: 'array', items: candidateSchema },
  },
  required: ['prompt', 'selectedTargets', 'nameCandidates'],
  additionalProperties: false,
}

const STATUS_KEYWORDS: Array<{ pattern: RegExp; status: string; label: string }> = [
  { pattern: /\bdone|completed?\b/i, status: 'COMPLETED', label: 'Done' },
  { pattern: /\bin\s*progress\b/i, status: 'IN_PROGRESS', label: 'In progress' },
  { pattern: /\btodo|to[- ]do\b/i, status: 'TODO', label: 'To do' },
  { pattern: /\bblocked\b/i, status: 'BLOCKED', label: 'Blocked' },
  { pattern: /\breview\b/i, status: 'REVIEW', label: 'Review' },
]

const timelineStatusAction: ActionDefinition<TimelineStatusArgs> = {
  id: TIMELINE_STATUS_ACTION_ID,
  label: 'Change timeline status',
  description: 'Prepares timeline items for a confirmed workflow-status change.',
  risk: 'MEDIUM',
  domain: 'timeline',
  surfaces: ['ai', 'catalog'],
  argsSchema: timelineStatusArgsSchema,
  permission: (context) => context.canPerform('timeline.edit'),
  run: async (args, context): Promise<ActionOutcome> => {
    const status = STATUS_KEYWORDS.find((entry) => entry.pattern.test(args.prompt))
    if (!status) {
      return { kind: 'FAILED', message: 'I could not tell which status to move to.', recoverable: true, correlationId: context.correlationId! }
    }
    const candidates = args.selectedTargets.length > 0 ? args.selectedTargets : args.nameCandidates
    if (candidates.length === 0) {
      return {
        kind: 'FAILED',
        message: `Select the timeline item you want moved to "${status.label}" and try again.`,
        recoverable: true,
        correlationId: context.correlationId!,
      }
    }
    const preview = candidates.map((candidate) => `• ${candidate.title}`).join('\n')
    const confirmPayload: TimelineStatusConfirmPayload = {
      kind: 'TIMELINE_STATUS_CHANGE',
      status: status.status,
      targetIds: candidates.map((candidate) => candidate.id),
    }
    return {
      kind: 'NEEDS_CONFIRMATION',
      message: `Move to "${status.label}":\n${preview}\n\nConfirm?`,
      confirmPayload,
      correlationId: context.correlationId!,
    }
  },
}

export const registerTimelineStatusAction = (): void => {
  if (!getAction(TIMELINE_STATUS_ACTION_ID)) registerAction(timelineStatusAction)
}

registerTimelineStatusAction()

// Timeline Orbit Actions (U4.7)
export const TIMELINE_EVENT_EDIT_ACTION_ID = 'timeline.event.edit'
export const TIMELINE_EVENT_DUPLICATE_ACTION_ID = 'timeline.event.duplicate'
export const TIMELINE_EVENT_MOVE_TO_BAND_ACTION_ID = 'timeline.event.move-to-band'
export const TIMELINE_EVENT_DELETE_ACTION_ID = 'timeline.event.delete'

export const TIMELINE_BAND_RENAME_ACTION_ID = 'timeline.band.rename'
export const TIMELINE_BAND_REORDER_ACTION_ID = 'timeline.band.reorder'
export const TIMELINE_BAND_COLLAPSE_ACTION_ID = 'timeline.band.collapse'

export const TIMELINE_CANVAS_NEW_EVENT_ACTION_ID = 'timeline.canvas.new-event'
export const TIMELINE_CANVAS_FIT_TO_RANGE_ACTION_ID = 'timeline.canvas.fit-to-range'

export type TimelineOrbitTargetArgs = {
  id?: string
  title?: string
}

const timelineOrbitTargetArgsSchema: JSONSchemaType<TimelineOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  TIMELINE_EVENT_EDIT_ACTION_ID,
  TIMELINE_EVENT_DUPLICATE_ACTION_ID,
  TIMELINE_EVENT_MOVE_TO_BAND_ACTION_ID,
  TIMELINE_EVENT_DELETE_ACTION_ID,
  TIMELINE_BAND_RENAME_ACTION_ID,
  TIMELINE_BAND_REORDER_ACTION_ID,
  TIMELINE_BAND_COLLAPSE_ACTION_ID,
])

const makeOrbitTimelineAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'timeline.edit' | 'timeline.delete' | 'timeline.move_restore',
): ActionDefinition<TimelineOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'timeline',
  surfaces: ['orbit', 'catalog'],
  argsSchema: timelineOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.timelineOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === TIMELINE_EVENT_EDIT_ACTION_ID) {
      handler = ops?.editEvent ? () => ops.editEvent!(args.id!) : undefined
    } else if (id === TIMELINE_EVENT_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateEvent ? () => ops.duplicateEvent!(args.id!) : undefined
    } else if (id === TIMELINE_EVENT_MOVE_TO_BAND_ACTION_ID) {
      handler = ops?.moveEventToBand ? () => ops.moveEventToBand!(args.id!) : undefined
    } else if (id === TIMELINE_EVENT_DELETE_ACTION_ID) {
      handler = ops?.deleteEvent ? () => ops.deleteEvent!(args.id!) : undefined
    } else if (id === TIMELINE_BAND_RENAME_ACTION_ID) {
      handler = ops?.renameBand ? () => ops.renameBand!(args.id!) : undefined
    } else if (id === TIMELINE_BAND_REORDER_ACTION_ID) {
      handler = ops?.reorderBand ? () => ops.reorderBand!(args.id!) : undefined
    } else if (id === TIMELINE_BAND_COLLAPSE_ACTION_ID) {
      handler = ops?.collapseBand ? () => ops.collapseBand!(args.id!) : undefined
    } else if (id === TIMELINE_CANVAS_NEW_EVENT_ACTION_ID) {
      handler = ops?.createEvent ? () => ops.createEvent!() : undefined
    } else if (id === TIMELINE_CANVAS_FIT_TO_RANGE_ACTION_ID) {
      handler = ops?.fitToRange ? () => ops.fitToRange!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: `${label} dispatched`,
      correlationId,
    }
  },
})

export const timelineEventEditAction = makeOrbitTimelineAction(
  TIMELINE_EVENT_EDIT_ACTION_ID,
  'Edit',
  'Edits timeline event details.',
  'LOW',
  'timeline.edit',
)

export const timelineEventDuplicateAction = makeOrbitTimelineAction(
  TIMELINE_EVENT_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Duplicates a timeline event.',
  'LOW',
  'timeline.edit',
)

export const timelineEventMoveToBandAction = makeOrbitTimelineAction(
  TIMELINE_EVENT_MOVE_TO_BAND_ACTION_ID,
  'Move to band',
  'Moves timeline event to a different band.',
  'LOW',
  'timeline.move_restore',
)

export const timelineEventDeleteAction = makeOrbitTimelineAction(
  TIMELINE_EVENT_DELETE_ACTION_ID,
  'Delete',
  'Deletes a timeline event.',
  'HIGH',
  'timeline.delete',
)

export const timelineBandRenameAction = makeOrbitTimelineAction(
  TIMELINE_BAND_RENAME_ACTION_ID,
  'Rename',
  'Renames a timeline band header.',
  'LOW',
  'timeline.edit',
)

export const timelineBandReorderAction = makeOrbitTimelineAction(
  TIMELINE_BAND_REORDER_ACTION_ID,
  'Reorder',
  'Reorders a timeline band.',
  'LOW',
  'timeline.edit',
)

export const timelineBandCollapseAction = makeOrbitTimelineAction(
  TIMELINE_BAND_COLLAPSE_ACTION_ID,
  'Collapse',
  'Toggles timeline band collapse state.',
  'READ_ONLY',
)

export const timelineCanvasNewEventAction = makeOrbitTimelineAction(
  TIMELINE_CANVAS_NEW_EVENT_ACTION_ID,
  'New event',
  'Creates a new event on the timeline.',
  'LOW',
  'timeline.edit',
)

export const timelineCanvasFitToRangeAction = makeOrbitTimelineAction(
  TIMELINE_CANVAS_FIT_TO_RANGE_ACTION_ID,
  'Fit to range',
  'Fits timeline view to event range.',
  'READ_ONLY',
)

export const registerTimelineOrbitActions = (): void => {
  if (!getAction(TIMELINE_EVENT_EDIT_ACTION_ID)) registerAction(timelineEventEditAction)
  if (!getAction(TIMELINE_EVENT_DUPLICATE_ACTION_ID)) registerAction(timelineEventDuplicateAction)
  if (!getAction(TIMELINE_EVENT_MOVE_TO_BAND_ACTION_ID)) registerAction(timelineEventMoveToBandAction)
  if (!getAction(TIMELINE_EVENT_DELETE_ACTION_ID)) registerAction(timelineEventDeleteAction)
  if (!getAction(TIMELINE_BAND_RENAME_ACTION_ID)) registerAction(timelineBandRenameAction)
  if (!getAction(TIMELINE_BAND_REORDER_ACTION_ID)) registerAction(timelineBandReorderAction)
  if (!getAction(TIMELINE_BAND_COLLAPSE_ACTION_ID)) registerAction(timelineBandCollapseAction)
  if (!getAction(TIMELINE_CANVAS_NEW_EVENT_ACTION_ID)) registerAction(timelineCanvasNewEventAction)
  if (!getAction(TIMELINE_CANVAS_FIT_TO_RANGE_ACTION_ID)) registerAction(timelineCanvasFitToRangeAction)
}

registerTimelineOrbitActions()
